<template>
  <container>
    <div v-if="!pastRun" class="pg-title-row">
      <div>
        <h1 class="pg-title">Past Runs</h1>
        <p class="pg-title-sub">Search completed races, review trainer sessions and open archived run data</p>
      </div>
      <span class="pr-total-badge">{{ totalElements }} runs</span>
    </div>

    <div v-if="!pastRun" class="pr-filter-card">
      <div class="pr-card-header">
        <span class="pr-icon-pill">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"/>
            <path d="M21 21l-4.3-4.3"/>
            <path d="M8 11h6"/>
          </svg>
        </span>
        <div>
          <h2 class="pr-card-title">Run Filters</h2>
          <p class="pr-card-subtitle">Narrow the archive by run name, ID, trainer or finish date</p>
        </div>
      </div>

      <div class="pr-search-controls">
        <div class="pr-control-group">
          <label class="pr-control-label">Search Run</label>
          <input type="text" v-model="filters.query" placeholder="Run name or ID" @keyup.enter="performSearch" class="pr-control-input">
        </div>

        <div class="pr-control-group">
          <label class="pr-control-label">Trainer</label>
          <div class="pr-select-wrap">
            <select v-model="filters.trainerId" @change="performSearch" class="pr-control-select">
              <option value="">All Trainers</option>
              <option v-for="t in allTrainers" :key="t.id" :value="t.id">
                {{ t.firstName }} {{ t.lastName }}
              </option>
            </select>
            <svg class="pr-select-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </div>
        </div>

        <div class="pr-control-group pr-control-group--date">
          <label class="pr-control-label">Date</label>
          <input type="date" v-model="filters.date" @change="performSearch" class="pr-control-input">
        </div>

        <button @click="performSearch" class="pr-search-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"/>
            <path d="M21 21l-4.3-4.3"/>
          </svg>
          Search
        </button>
      </div>
    </div>

    <div v-if="!pastRun && runsLoading" class="pr-table-card">
      <div class="pr-card-header">
        <span class="pr-icon-pill">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18"/>
            <path d="M3 12h18"/>
            <path d="M3 18h18"/>
          </svg>
        </span>
        <div>
          <h2 class="pr-card-title">Finished Runs</h2>
          <p class="pr-card-subtitle">Loading archived race records</p>
        </div>
      </div>
      <div class="pr-skeleton-table">
        <div class="pr-skeleton-head">
          <div v-for="n in 7" :key="'pr-head-' + n" class="pr-sk-cell sk-shimmer"></div>
        </div>
        <div v-for="row in 8" :key="'pr-row-' + row" class="pr-skeleton-row">
          <div v-for="col in 7" :key="'pr-cell-' + row + '-' + col" class="pr-sk-cell sk-shimmer"></div>
        </div>
      </div>
    </div>

    <race-table-list v-if="!pastRun && !runsLoading" subtitle="finished" :races="racesFinished" message-empty-list="There are no finished runs for these filters" type="resume"></race-table-list>
    
    <!-- Smart Pagination -->
    <div v-if="!pastRun && totalElements > 0" class="pagination-container">
      <paginate
        v-model="filters.pageNo"
        :page-count="Math.ceil(totalElements / filters.pageSize)"
        :page-range="7"
        :margin-pages="2"
        :click-handler="changePageHandler"
        :prev-text="'Prev'"
        :next-text="'Next'"
        :container-class="'pagination custom-pagination'"
        :page-class="'page-item'">
      </paginate>
    </div>
    
    <div class="live-run__content pastRunContent">
      <race-header v-if="pastRun" :isPastRun="pastRun"></race-header>
      <runners-table v-if="pastRun" :isPastRun="pastRun"></runners-table>
    </div>
  </container>
</template>

<script>
import Container from '@/components/Container'
import RaceTableList from '@/components/Race/RaceTableList'
import RaceHeader from '@/components/LiveRun/Race/Header'
import { mapActions, mapGetters } from 'vuex'
import RunnersTable from '@/components/LiveRun/Race/RunnersTable'
import racesByTrainerService from '@/services/racesByTrainer'
import Paginate from 'vuejs-paginate'

export default {
  name: 'past-run',
  props: ['pastRun'],
  data () {
    return {
      racesFinished: [],
      runsLoading: true,
      totalElements: 0,
      filters: {
        query: '',
        trainerId: '',
        date: '',
        pageNo: 1,
        pageSize: 25
      }
    }
  },
  computed: {
    ...mapGetters({
      allTrainers: 'trainers/allTrainers'
    })
  },
  methods: {
    initialize () {
      this.fetchAllTrainersList()
      this.fetchRuns()
    },
    fetchRuns () {
      this.runsLoading = true
      this.addTaskToQueue('fetchRuns')
      
      const params = {
        pageNo: this.filters.pageNo,
        pageSize: this.filters.pageSize,
        query: this.filters.query,
        trainerId: this.filters.trainerId,
        date: this.filters.date
      }
      
      racesByTrainerService.getAllRuns(params)
        .then((response) => {
          this.totalElements = response.body.totalElements || 0
          this.racesFinished = response.body.content || []
        })
        .catch(err => console.error(err))
        .finally(() => {
          this.runsLoading = false
          this.removeTaskFromQueue('fetchRuns')
        })
    },
    performSearch () {
      this.filters.pageNo = 1
      this.fetchRuns()
    },
    changePageHandler (pageNum) {
      this.filters.pageNo = pageNum
      this.fetchRuns()
    },
    ...mapActions({
      fetchAllTrainersList: 'trainers/fetchAllTrainersList',
      addTaskToQueue: 'LoadingQueue/addTaskToQueue',
      removeTaskFromQueue: 'LoadingQueue/removeTaskFromQueue'
    })
  },
  created () {
    this.initialize()
  },
  components: {
    Container,
    RaceHeader,
    RaceTableList,
    RunnersTable,
    Paginate
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}
</style>

<style scoped>
.pr-total-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  height: 32px;
  padding: 0 13px;
  border: 1px solid #e8e8ed;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  font-size: 12px;
  font-weight: 700;
}

.pr-filter-card,
.pr-table-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
  margin-bottom: 1.5rem;
}

.pr-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.35rem;
}

.pr-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.pr-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.2;
}

.pr-card-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 4px 0 0;
}

.pr-search-controls {
  display: grid;
  grid-template-columns: minmax(220px, 1.3fr) minmax(190px, 1fr) minmax(150px, .75fr) auto;
  gap: 14px;
  align-items: end;
}

.pr-control-group {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pr-control-label {
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 7px;
}

.pr-control-input,
.pr-control-select {
  width: 100%;
  height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  padding: 0 12px;
  box-sizing: border-box;
  transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
}

.pr-control-input:focus,
.pr-control-select:focus {
  outline: none;
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, .11);
}

.pr-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.pr-control-select {
  appearance: none;
  padding-right: 34px;
}

.pr-select-icon {
  position: absolute;
  right: 12px;
  pointer-events: none;
  color: #8a8a9a;
}

.pr-search-btn {
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  border: none;
  background: #5b4fcf;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .18s ease, transform .18s ease;
}

.pr-search-btn:hover {
  background: #4338ca;
}

.pr-search-btn:active {
  transform: translateY(1px);
}

.pr-skeleton-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pr-skeleton-head,
.pr-skeleton-row {
  display: grid;
  grid-template-columns: .65fr .9fr .9fr .85fr 1.6fr .9fr .9fr;
  gap: 12px;
  align-items: center;
}

.pr-skeleton-head {
  padding-bottom: 4px;
}

.pr-sk-cell {
  height: 16px;
}

.pr-skeleton-row {
  min-height: 42px;
  border-top: 1px solid #f0f0f5;
}

.pagination-container {
  margin-top: .5rem;
  display: flex;
  justify-content: center;
}

::v-deep .pagination {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}

::v-deep .pagination li {
  display: inline-block;
}

::v-deep .pagination li a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e8e8ed;
  background: #fff !important;
  color: #1a1a2e !important;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all .18s ease;
}

::v-deep .pagination li a:hover {
  background: #f0effe !important;
  border-color: #c8c3f7;
  color: #413b94 !important;
}

::v-deep .pagination .active,
::v-deep .pagination li.active,
::v-deep .pagination li.active a,
::v-deep .pagination li.active span {
  background: #5b4fcf !important;
  background-color: #5b4fcf !important;
  color: #fff !important;
  border-color: #5b4fcf !important;
}

::v-deep li.page-item.active a,
::v-deep .pagination li.page-item.active a {
  color: #fff !important;
}

::v-deep .pagination li.disabled a {
  color: #b8b8c6 !important;
  cursor: not-allowed;
  background: #fafafa !important;
  border-color: #f0f0f5;
}

@media (max-width: 960px) {
  .pr-search-controls {
    grid-template-columns: 1fr 1fr;
  }

  .pr-search-btn {
    width: 100%;
  }
}

@media (max-width: 680px) {
  .pg-title-row {
    align-items: flex-start;
    gap: 12px;
    flex-direction: column;
  }

  .pr-filter-card,
  .pr-table-card {
    padding: 1.25rem;
  }

  .pr-search-controls {
    grid-template-columns: 1fr;
  }

  .pr-skeleton-head,
  .pr-skeleton-row {
    grid-template-columns: 1fr 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// PastRun.vue?50d96b23